// Store sections: reveal on scroll, one shared finish (pink/black) and the five-mode tabs. No dependencies.
(() => {
  document.documentElement.classList.add('reveal-ready');   // reveals only hide content once this script runs

  // Reveal: elements fade up once when they enter the viewport.
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
  document.querySelectorAll('.reveal').forEach((el) => io.observe(el));

  // Design views: decode every image of the figure (base + lit layers) before it is shown, then fade it in.
  const views = document.querySelector('.views');
  if (views) {
    const vio = new IntersectionObserver(async ([e]) => {
      if (!e.isIntersecting) return;
      vio.disconnect();
      await loadLit();                 // lit layers decoded too, so nothing pops in afterwards
      await Promise.all([...views.querySelectorAll('.views-img')].map((im) => im.decode().catch(() => {})));
      views.classList.add('in');
    }, { rootMargin: '600px 0px' });
    vio.observe(views);
  }

  // Finish: the same choice updates the presentation, design-detail and five-lights images.
  let finish = 'pink', mode = 'glow';
  const head = document.querySelector('.lights-head');
  const swapHead = () => {
    if (!head) return;
    const src = window.ssrAsset(`ssr-head-${finish}-${mode}.jpg`);
    const pre = new Image();
    pre.onload = () => { head.classList.add('swap'); setTimeout(() => { head.src = src; head.classList.remove('swap'); }, 180); };
    pre.src = src;
  };
  const swatches = [...document.querySelectorAll('.ssr-finish-option')];
  const finishSelect = document.querySelector('.ssr-buy .ssr-variant');
  const setFinish = (f, focus) => {
    finish = f;
    if (finishSelect) {
      const option = [...finishSelect.options].find((o) => o.dataset.finish === f);
      finishSelect.value = option ? option.value : '';
      const add = finishSelect.form?.querySelector('[type="submit"]');
      if (add) add.disabled = !option;
    }
    swatches.forEach((b) => { const on = b.dataset.finish === f; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', String(on)); b.tabIndex = on ? 0 : -1; if (on && focus) b.focus(); });
    document.querySelectorAll('[data-finish-img]').forEach((img) => img.classList.toggle('is-on', img.dataset.finishImg === f));
    swapHead();
    document.dispatchEvent(new Event('ssr:finish'));
  };
  swatches.forEach((b, i) => {
    b.addEventListener('click', () => setFinish(b.dataset.finish));
    b.addEventListener('keydown', (e) => {
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault(); setFinish(swatches[(i + 1) % swatches.length].dataset.finish, true);
    });
  });

  finishSelect?.addEventListener('change', () => {
    const selected = finishSelect.selectedOptions[0]?.dataset.finish;
    if (selected) setFinish(selected);
  });
  if (finishSelect?.selectedOptions[0]?.dataset.finish) setFinish(finishSelect.selectedOptions[0].dataset.finish);

  // Five modes (names and functions as listed for the device; pending supplier verification).
  const MODES = {
    glow: ['01', 'Glow', 'Orange light', 'EMS + Vibration'],
    line: ['02', 'Line', 'Green light', 'EMS + Vibration'],
    contour: ['03', 'Contour', 'Red light', 'EMS + Vibration'],
    pore: ['04', 'Pore', 'Blue light', 'Electroporation + Vibration'],
    clarify: ['05', 'Clarify', 'Purple light', 'Electroporation + Vibration'],
  };
  const tabs = [...document.querySelectorAll('.mode-tabs [role="tab"]')];
  const panel = document.getElementById('mode-panel');
  const setMode = (t, focus) => {
    mode = t.dataset.mode;
    tabs.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', String(on)); x.tabIndex = on ? 0 : -1; });
    if (focus) t.focus();
    panel.setAttribute('aria-labelledby', t.id);
    panel.style.setProperty('--mc', getComputedStyle(t).getPropertyValue('--mc'));
    const [n, name, light, tech] = MODES[mode];
    panel.classList.add('swap');
    setTimeout(() => {
      panel.querySelector('.mode-num').textContent = n;
      panel.querySelector('.mode-name').textContent = name;
      panel.querySelector('.mode-light').textContent = light;
      panel.querySelector('.mode-tech').textContent = tech;
      panel.classList.remove('swap');
    }, 160);
    swapHead();
  };
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => setMode(t));
    t.addEventListener('keydown', (e) => {
      const k = { ArrowRight: 1, ArrowLeft: -1, Home: -i, End: tabs.length - 1 - i }[e.key];
      if (k === undefined) return;
      e.preventDefault(); setMode(tabs[(i + k + tabs.length) % tabs.length], true);
    });
  });
  if (panel && tabs[0]) panel.style.setProperty('--mc', getComputedStyle(tabs[0]).getPropertyValue('--mc'));

  // Design in detail: the three heads cycle through the five lights, one second per colour.
  const LIT = [['glow', 'Glow', 'Orange light', '#e07a2a'], ['line', 'Line', 'Green light', '#3c9a5f'], ['contour', 'Contour', 'Red light', '#d0362c'],
    ['pore', 'Pore', 'Blue light', '#3c6fc4'], ['clarify', 'Clarify', 'Purple light', '#8a52b8']];
  const litImgs = [...document.querySelectorAll('.views-lit img')];
  const label = document.querySelector('.views-mode');
  const design = document.getElementById('design');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let litIndex = 0, litTimer = 0, litLoaded = false, litVisible = false, litPlaying = false;   // lights play only when asked
  const showLit = () => {
    const [m, name, light, col] = LIT[litIndex];
    document.querySelectorAll('[data-light]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.light === m)));
    litImgs.forEach((im) => im.classList.toggle('is-lit', im.dataset.litFinish === finish && im.dataset.litMode === m));
    if (label) { label.style.setProperty('--vm', col); label.querySelector('.vm-name').textContent = name; label.querySelector('.vm-light').textContent = light; }
  };
  let litPromise = null;
  const loadLit = () => (litPromise ||= Promise.all(litImgs.map((im) => { im.src = im.dataset.src; return im.decode().catch(() => {}); })));
  const startLit = async () => {
    if (!litLoaded) { litLoaded = true; await loadLit(); }
    showLit();
    if (reduceMotion.matches || document.hidden || !litPlaying || litTimer || !litVisible) return;   // section may have left the view while images loaded
    litTimer = setInterval(() => { litIndex = (litIndex + 1) % LIT.length; showLit(); }, 2800);
  };
  const stopLit = () => { clearInterval(litTimer); litTimer = 0; };
  // Light controls: pick one light, or play them in turn.
  const playBtn = document.querySelector('[data-light-play]');
  const syncPlay = () => { if (!playBtn) return; playBtn.textContent = litPlaying ? 'Pause the lights' : 'Play the lights'; playBtn.setAttribute('aria-pressed', String(litPlaying)); };
  document.querySelectorAll('[data-light]').forEach((b) => b.addEventListener('click', async () => {
    litPlaying = false; stopLit(); syncPlay(); await loadLit(); litLoaded = true;
    litIndex = Math.max(0, LIT.findIndex((v) => v[0] === b.dataset.light)); showLit();
  }));
  playBtn?.addEventListener('click', () => { litPlaying = !litPlaying; syncPlay(); litPlaying ? startLit() : stopLit(); });
  reduceMotion.addEventListener('change', () => { if (reduceMotion.matches) { litPlaying = false; stopLit(); syncPlay(); } });
  if (design && litImgs.length) {
    // start fetching the lit layers well before the section arrives, so the figure appears complete
    new IntersectionObserver(([e]) => { if (e.isIntersecting) loadLit(); }, { rootMargin: '1200px 0px' }).observe(design);
    new IntersectionObserver(([e]) => { litVisible = e.isIntersecting; litVisible ? startLit() : stopLit(); }, { rootMargin: '0px' }).observe(design);
    document.addEventListener('visibilitychange', () => (document.hidden ? stopLit() : design.getBoundingClientRect().top < innerHeight && startLit()));
    // keep the lit layers on the chosen finish
    document.addEventListener('ssr:finish', () => litLoaded && showLit());
  }
})();

// Sticky header: shown once the hero has been scrolled past.
(() => {
  const head = document.querySelector('.site-head'), finish = document.getElementById('finish');
  if (!head || !finish) return;
  new IntersectionObserver(([e]) => head.classList.toggle('on', e.isIntersecting || e.boundingClientRect.top < 0), { rootMargin: '0px 0px -40% 0px' }).observe(finish);
})();

// Product photos: each slot shows assets/products/<slug>.jpg once that file exists; until then a quiet placeholder.
document.querySelectorAll('.r-photo[data-photo]').forEach((fig) => {
  const name = fig.closest('.product')?.querySelector('h3')?.textContent || '';
  const img = new Image();
  img.alt = name; img.decoding = 'async';
  img.onload = () => {
    const z = Number(fig.dataset.zoom || 0);
    if (z && fig.dataset.focus) {
      // show one product from a group photo: scale the square image and centre it on the focus point
      const [fx, fy] = fig.dataset.focus.split(' ').map((v) => parseFloat(v) / 100);
      const ratio = 5 / 4;   // photo slots are 4:5 (height / width)
      const wPct = z * ratio * 100, hPct = z * 100;
      Object.assign(img.style, { inset: 'auto', maxWidth: 'none', objectFit: 'fill', width: wPct + '%', height: hPct + '%', left: (50 - fx * wPct) + '%', top: (50 - fy * hPct) + '%' });
    }
    fig.prepend(img); fig.classList.add('has-img');
  };
  const p = fig.dataset.photo;
  img.src = window.ssrAsset(`ssr-products-${p.includes('.') ? p : p + '.jpg'}`);
});

// Purchase options (visual only while orders are closed): device or The Ritual Set.
(() => {
  const opts = [...document.querySelectorAll('.buy-opt')], price = document.querySelector('[data-price]');
  const PRICES = { device: '$154.00', set: '$179.00' };
  const pick = (o, focus) => {
    opts.forEach((x) => { const on = x === o; x.classList.toggle('is-on', on); x.setAttribute('aria-checked', String(on)); x.tabIndex = on ? 0 : -1; });
    if (price) price.textContent = PRICES[o.dataset.buy];
    if (focus) o.focus();
  };
  opts.forEach((o, i) => {
    o.addEventListener('click', () => pick(o));
    o.addEventListener('keydown', (e) => {
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault(); pick(opts[(i + 1) % opts.length], true);
    });
  });
  // "Choose The Ritual Set" link selects the set option
  document.querySelectorAll('a[href="#finish"]').forEach((a) => { if (/Ritual Set/.test(a.textContent)) a.addEventListener('click', () => { const s = opts.find((o) => o.dataset.buy === 'set'); if (s) pick(s); }); });
})();

// Links to #how or #specs open their disclosure before scrolling to it.
(() => {
  const open = () => { const el = location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1))); if (el && el.tagName === 'DETAILS') el.open = true; };
  document.querySelectorAll('a[href="#how"], a[href="#specs"]').forEach((a) => a.addEventListener('click', () => { const el = document.querySelector(a.getAttribute('href')); if (el) el.open = true; }));
  addEventListener('hashchange', open); open();
})();

// What it does: hovering, focusing or tapping a technology relights the close-up in a mode that uses it.
(() => {
  const items = [...document.querySelectorAll('.tech-item')], imgs = [...document.querySelectorAll('[data-tech-img]')];
  const cap = document.querySelector('[data-tech-cap]');
  if (!items.length) return;
  const pick = (it) => {
    items.forEach((x) => x.classList.toggle('is-on', x === it));
    imgs.forEach((im) => im.classList.toggle('is-on', im.dataset.techImg === it.dataset.tech));
    if (cap) cap.textContent = it.querySelector('h3').textContent;
  };
  items.forEach((it) => { ['mouseenter', 'focus', 'click'].forEach((ev) => it.addEventListener(ev, () => pick(it))); });
})();

// How to use: hovering, focusing or tapping a step highlights its control on the device.
(() => {
  const steps = [...document.querySelectorAll('.guide-step')], spots = [...document.querySelectorAll('.hotspot')];
  if (!steps.length) return;
  const pick = (s) => {
    steps.forEach((x) => x.classList.toggle('is-on', x === s));
    spots.forEach((p) => p.classList.toggle('is-on', p.dataset.spot === s.dataset.spot));
  };
  steps.forEach((s) => ['mouseenter', 'focus', 'click'].forEach((ev) => s.addEventListener(ev, () => pick(s))));
  pick(steps[0]);
})();
